import moment from 'moment-timezone'
import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core'
import { Close as CloseIcon, ExpandMore as ExpandMoreIcon } from '@material-ui/icons'

import { DraggablePaper } from 'civic-champs-shared'
import Loading from 'components/Loading'
import { useEffectOnce } from 'react-use'
import { EventListTable } from './EventListTable'
import { EventDiffResult, useCompareEvents } from 'Event/hooks/useCompareEvents'
import { EventListConflictTable } from './EventListConflictTable'

const useStyles = makeStyles({
  dialog: {
    background: '#F8FAFF',
    padding: '0 20px 20px',
    position: 'relative',
    maxWidth: '100%',
    width: '1100px', //TODO is this right?
    scrollbarColor: '#74777F transparent',
    // @ts-ignore
    scrollbarWidth: '8px',
  },
  title: {
    cursor: 'move',
    padding: '20px 0 0',
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: '24px',
    lineHeight: '32px',
    color: '#000000',
    marginBottom: '20px',
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  closeIcon: {
    fontSize: '16px',
  },
})

interface EventListDifferencesPromptProps {
  showing: boolean
  close: () => void
  from: moment.MomentInput
  to: moment.MomentInput
}

export function EventListDifferencesPrompt({ showing, close, from, to }: EventListDifferencesPromptProps) {
  const classes = useStyles()

  const [diff, setDiff] = useState<EventDiffResult | null>(null)
  const [compareEvents, loading] = useCompareEvents()

  useEffectOnce(() => {
    ;(async () => {
      setDiff(null)
      const result = await compareEvents(from, to)
      setDiff(result)
    })()
  })

  return (
    <Dialog
      open={showing}
      PaperComponent={DraggablePaper}
      // @ts-ignore
      PaperProps={{ handle: '#event-list-diff-prompt' }}
      maxWidth="lg"
      classes={{ paper: classes.dialog }}
      disableEnforceFocus
    >
      <DialogTitle classes={{ root: classes.title }} disableTypography={true} id="event-list-diff-prompt">
        Opportunities API vs Client-Side Processing
        <IconButton
          className={classes.closeButton}
          onClick={() => {
            close()
          }}
        >
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading || !diff ? (
          <Loading />
        ) : (
          <>
            <ExpansionPanel defaultExpanded={false}>
              <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  Matching Events&nbsp;
                  <span>({diff.matched.length || 0})</span>
                </Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <EventListTable events={diff.matched} />
              </ExpansionPanelDetails>
            </ExpansionPanel>
            <ExpansionPanel defaultExpanded={false}>
              <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  Missing&nbsp;
                  <span>({diff.missing.length || 0})</span>
                </Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <EventListTable events={diff.missing} />
              </ExpansionPanelDetails>
            </ExpansionPanel>
            <ExpansionPanel defaultExpanded={false}>
              <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  Extra&nbsp;
                  <span>({diff.extra.length || 0})</span>
                </Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <EventListTable events={diff.extra} />
              </ExpansionPanelDetails>
            </ExpansionPanel>
            <ExpansionPanel defaultExpanded={false}>
              <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  Conflicts&nbsp;
                  <span>({diff.conflicts.length || 0})</span>
                </Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <EventListConflictTable conflicts={diff.conflicts} />
              </ExpansionPanelDetails>
            </ExpansionPanel>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
